import { RG_TYPE, getRgObjectsOfType } from "@project-rouge/rg-core";
import { observer } from "mobx-react-lite";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { when } from "mobx";
import { COST_TRACKING_VARIANTS } from "src/screens/cost-tracking-variants";
import type { ScenarioConfiguration } from "src/service/cost/data/building";
import type { ArchitecturalMetricsSnapshot } from "src/types/ArchitecturalMetrics";
import { DesignsGenerator } from "src/types/DesignsGenerator";
import type { GeneratedItem } from "src/types/GeneratedItem";
import type { RgBuilding } from "src/types/RgCorePackage";
import { CreateBoxWorld } from "src/utils/CreateBoxWorld";
import { GetCostDataHash } from "src/utils/GetCostDataHash";
import { RenderRgWorld } from "src/utils/RenderRgWorld";
import { RgWorldFromZip } from "src/utils/RgWorldFromZip";
import { RgWorldToBeCostPayload } from "src/utils/RgWorldToBeCostPayload";

// Detail screen for one cost-tracking variant. Walks through the cost flow
// as a "today / what's missing / once filled" three-column pedagogical
// layout — each section has the same shape so it's easy to read across.
// Left column = current behavior with real values, middle column = the
// gap and where it fills from (BKI / BRW / M / user), right column = what
// the section becomes once filled.
//
// For live numbers (UK + German side-by-side, manual override), see the
// compare-engines screen.
//
// Variant is regenerated fresh on mount via the seeded generator, so the
// building shown matches the grid tile of the same index.

export const CostTrackingDetailScreen = observer(function CostTrackingDetailScreen() {
  const { variantIndex } = useParams<{ variantIndex: string }>();
  const idx = Number(variantIndex);
  const variant = COST_TRACKING_VARIANTS[idx];

  const [item, setItem] = useState<GeneratedItem | null>(null);
  const [payload, setPayload] = useState<ScenarioConfiguration | null>(null);
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [costDataHash, setCostDataHash] = useState<string | null>(null);

  useEffect(() => {
    if (!variant) return;
    let cancelled = false;
    const generator = new DesignsGenerator({
      projectId: "cost-tracking-detail",
      listSize: 1,
    });
    (async () => {
      try {
        const world = CreateBoxWorld(variant.length, variant.width, variant.floors);
        generator.generate(world);
        await new Promise<void>((resolve) =>
          when(
            () => !generator.isRunning || generator.list.length > 0,
            () => resolve()
          )
        );
        if (cancelled) return;
        const generated = generator.list[0];
        if (!generated) {
          setError("Generator produced no option");
          return;
        }
        setItem(generated);
        const rgWorld = RgWorldFromZip(generated.rgWorldZip);
        const buildings = getRgObjectsOfType<RgBuilding>(rgWorld, RG_TYPE.Building);
        setPayload(RgWorldToBeCostPayload(rgWorld, buildings));
        const thumb = await RenderRgWorld(rgWorld, [1, -1, 1], 480);
        if (!cancelled) setThumbnail(thumb);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    GetCostDataHash().then((h) => {
      if (!cancelled) setCostDataHash(h);
    });
    return () => {
      cancelled = true;
      generator.terminate();
    };
  }, [variant]);

  if (!variant) {
    return (
      <div className="p-6 font-mono text-xs">
        <p className="mb-2">Unknown variant index: {variantIndex}</p>
        <Link to="/admin/cost-tracking" className="text-neutral-6 hover:text-neutral-8 underline">
          ← back to cost tracking
        </Link>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-y-auto bg-white text-neutral-8 font-mono text-xs">
      <header className="sticky top-0 bg-white border-b border-neutral-3 px-6 py-4 flex items-baseline gap-4 z-10">
        <Link
          to="/admin/cost-tracking"
          className="text-neutral-6 hover:text-neutral-8 shrink-0 underline-offset-2 hover:underline"
        >
          ← back
        </Link>
        <h1 className="text-lg font-bold">Cost detail · {variant.label}</h1>
        <span className="text-neutral-6 shrink-0">
          {variant.length}×{variant.width}m · {variant.floors} floors
        </span>
        <Link
          to={`/admin/cost-engine-compare/${idx}`}
          className="text-neutral-6 hover:text-neutral-8 shrink-0 underline-offset-2 hover:underline"
        >
          compare engines →
        </Link>
        <span className="ml-auto text-neutral-6">
          cost_data hash: <code className="text-neutral-8">{costDataHash ?? "…"}</code>
        </span>
      </header>

      <div className="p-6 max-w-7xl mx-auto">
        <p className="text-neutral-6 mb-6 max-w-3xl">
          A walkthrough of the cost flow for one variant, organized as{" "}
          <em>today / what&apos;s missing / once filled</em>. Each section has the same shape
          so you can see at every step what we have, what data is still missing, and what
          completes the German DIN 276 cost plan. The cost engine itself isn&apos;t changing
          — we&apos;re adding data, presentation, and a few new input fields around it.
        </p>

        {error && <p className="text-danger-7 mb-4">Error: {error}</p>}

        {thumbnail && (
          <div className="mb-8 max-w-md border border-neutral-3 rounded overflow-hidden">
            <img src={thumbnail} alt={variant.label} className="w-full" />
          </div>
        )}

        <PedagogicalSection title="1. Brief inputs (what you control)">
          <Today>
            <KV k="length" v={`${variant.length} m`} />
            <KV k="width" v={`${variant.width} m`} />
            <KV k="floors" v={String(variant.floors)} />
            <KV k="kind" v={variant.kind} />
          </Today>
          <Missing>
            <p className="text-neutral-7">
              Nothing — these are the user inputs by design. The brief stays simple.
            </p>
          </Missing>
          <Filled>
            <p className="text-neutral-7">
              Same. The brief is the entry point; new data comes in elsewhere.
            </p>
          </Filled>
        </PedagogicalSection>

        {item?.architecturalMetrics && (
          <PedagogicalSection title="2. Geometry (derived automatically)">
            <Today>
              <GeometryTable arch={item.architecturalMetrics} />
            </Today>
            <Missing>
              <p className="text-neutral-7">
                Nothing — geometry is fully derived from the brief by the procedural
                generator. ~60 architectural fields available; ~50 feed the cost engine.
              </p>
            </Missing>
            <Filled>
              <p className="text-neutral-7">
                Same. New cost-data rows just reference different fields from this set.
              </p>
            </Filled>
          </PedagogicalSection>
        )}

        <PedagogicalSection title="3. Cost data & rates (the JSONs)">
          <Today>
            <p className="text-neutral-7 mb-2">
              6 JSON files in <code>/public/cost_data/</code> tagged with{" "}
              <strong>RIBA</strong> element groups:
            </p>
            <ul className="space-y-0.5 text-neutral-6">
              <li>· kit_module.json</li>
              <li>· kit_unit.json</li>
              <li>· kit_unit_module.json</li>
              <li>· kit_module_element_reference.json</li>
              <li>· kit_module_bill_of_material.json</li>
              <li>· kit_base_build_element_reference.json</li>
            </ul>
            <p className="text-neutral-6 mt-2">
              Hash: <code className="text-neutral-8">{costDataHash ?? "…"}</code>
            </p>
          </Today>
          <Missing>
            <ul className="space-y-1 text-neutral-7">
              <li>
                · <strong>BKI rates</strong> for KG 200/300/400/500 (the starting point for
                German construction rates)
              </li>
              <li>
                · <strong>KG codes</strong> tagged on existing rows (so KG 300/400 split
                cleanly)
              </li>
              <li>
                · <strong>BRW</strong> API integration for KG 100 (Bodenrichtwert lookup)
              </li>
            </ul>
          </Missing>
          <Filled>
            <p className="text-neutral-7">
              Same files + new BKI rows + KG tags + BRW lookup. Engine consumes them via the
              same code path.
            </p>
          </Filled>
        </PedagogicalSection>

        <PedagogicalSection title="4. User-facing inputs (in the cost flow)">
          <Today>
            <p className="text-neutral-7">None on this screen.</p>
            <p className="text-neutral-6 mt-2">
              On-cost percentages (prelims/contingency/OHP) are hard-coded constants — no UI
              to change them.
            </p>
          </Today>
          <Missing>
            <ul className="space-y-1 text-neutral-7">
              <li>
                · <strong>KG 600 lump sum</strong> — furnishing budget
              </li>
              <li>
                · <strong>KG 800 quote</strong> — financing cost from capital provider
              </li>
              <li>
                · <strong>KG 100 manual override</strong> (optional) — when BRW unavailable
              </li>
              <li>· On-cost editor (prelims/contingency/OHP %)</li>
            </ul>
          </Missing>
          <Filled>
            <p className="text-neutral-7">
              Form fields per row. Auto-fetched values (BRW, BKI) are overridable; lump
              sum + financing are user-only.
            </p>
          </Filled>
        </PedagogicalSection>

        <PedagogicalSection title="5. Calculation flow">
          <Today>
            <p className="text-neutral-7 mb-1">
              Engine consumes geometry → applies RIBA-tagged rates → produces:
            </p>
            <ul className="text-neutral-6 space-y-0.5">
              <li>· Modules (1.1)</li>
              <li>· Fit out (1.2)</li>
              <li>· Base build (1.3)</li>
              <li>· Prelims (10% of construction)</li>
              <li>· Contingency (3%, cascading)</li>
              <li>· OHP (5.5%, cascading)</li>
            </ul>
          </Today>
          <Missing>
            <ul className="space-y-1 text-neutral-7">
              <li>· 5 KG calc steps that don&apos;t exist yet (KG 100/200/500/600/800)</li>
              <li>· KG 300 ↔ KG 400 split (currently one tangled bucket)</li>
              <li>· On-cost recalc using sheet&apos;s flat-base formula (% × Σ(KG 200..500))</li>
            </ul>
          </Missing>
          <Filled>
            <p className="text-neutral-7">
              Same engine + small presentation layer. KG 300/400 split happens naturally
              once cost-data has KG tags.
            </p>
          </Filled>
        </PedagogicalSection>

        <section className="mb-8">
          <h2 className="text-sm font-bold uppercase tracking-wide text-neutral-7 mb-3">
            6. Output (the actual numbers)
          </h2>
          <p className="text-neutral-7">
            See it live:{" "}
            <Link
              to={`/admin/cost-engine-compare/${idx}`}
              className="font-semibold underline-offset-2 hover:underline"
            >
              compare engines →
            </Link>{" "}
            for the UK and German outputs side-by-side, with manual override to fill any
            missing rates and watch the gap close in real time.
          </p>
        </section>

        {payload && (
          <details className="mt-12 border border-neutral-3 rounded p-3">
            <summary className="cursor-pointer text-neutral-7 font-semibold">
              Raw cost engine payload (developer view)
            </summary>
            <pre className="mt-3 text-[10px] text-neutral-6 overflow-x-auto">
              {JSON.stringify(payload, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
});

// ─── Building blocks ─────────────────────────────────────────────────────

function PedagogicalSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-8">
      <h2 className="text-sm font-bold uppercase tracking-wide text-neutral-7 mb-3">
        {title}
      </h2>
      <div className="grid grid-cols-3 gap-3">{children}</div>
    </section>
  );
}

function Today({ children }: { children: ReactNode }) {
  return (
    <div className="border border-neutral-3 rounded p-3">
      <h3 className="text-[10px] font-bold uppercase tracking-wide text-neutral-7 mb-2">
        Today
      </h3>
      {children}
    </div>
  );
}

function Missing({ children }: { children: ReactNode }) {
  return (
    <div className="border border-secondary-3 rounded p-3 bg-secondary-1">
      <h3 className="text-[10px] font-bold uppercase tracking-wide text-secondary-7 mb-2">
        What&apos;s missing
      </h3>
      {children}
    </div>
  );
}

function Filled({ children }: { children: ReactNode }) {
  return (
    <div className="border border-neutral-3 rounded p-3">
      <h3 className="text-[10px] font-bold uppercase tracking-wide text-neutral-7 mb-2">
        Once filled
      </h3>
      {children}
    </div>
  );
}

function KV({
  k,
  v,
  muted,
  bold,
}: {
  k: string;
  v: string;
  muted?: boolean;
  bold?: boolean;
}) {
  return (
    <div className="flex justify-between gap-2">
      <span className={muted ? "text-neutral-5" : "text-neutral-6"}>{k}</span>
      <span
        className={`tabular-nums ${bold ? "font-bold" : ""} ${
          muted ? "text-neutral-6" : ""
        }`}
      >
        {v}
      </span>
    </div>
  );
}

function GeometryTable({ arch }: { arch: ArchitecturalMetricsSnapshot }) {
  const rows: { k: string; v: string }[] = [
    { k: "Site area (Plotsize)", v: fmtMetric((arch.siteAreaHectares ?? 0) * 10000, "m²") },
    { k: "Footprint", v: fmtMetric(arch.FootPrintTotal, "m²") },
    { k: "Land coverage (GRZ)", v: arch.LandCoverageTotal.toFixed(2) },
    { k: "GIA (total)", v: fmtMetric(arch.GIATotal, "m²") },
    { k: "GEA ≈ BGF (total)", v: fmtMetric(arch.GEATotal, "m²") },
    { k: "NIA ≈ WFL (total)", v: fmtMetric(arch.NIATotal, "m²") },
    { k: "Facade", v: fmtMetric(arch.FacadeTotal, "m²") },
    { k: "Glazed facade", v: fmtMetric(arch.GlazedFacade, "m²") },
    { k: "Roof", v: fmtMetric(arch.RoofAreaTotal, "m²") },
    { k: "Building height", v: fmtMetric(arch.BuildingHeightMax, "m") },
    { k: "Floors", v: fmtMetric(arch.floorsCount) },
    { k: "Apartments", v: fmtMetric(arch.ApartmentQuantityTotal) },
    { k: "Modules", v: fmtMetric(arch.ModuleQuantityTotal) },
    { k: "Cores", v: fmtMetric(arch.CoresTotal) },
  ];
  return (
    <div className="space-y-0.5">
      {rows.map((r) => (
        <KV key={r.k} k={r.k} v={r.v} />
      ))}
    </div>
  );
}

function fmtMetric(value: number, suffix?: string): string {
  if (!Number.isFinite(value)) return "—";
  const formatted = Math.round(value).toLocaleString();
  return suffix ? `${formatted} ${suffix}` : formatted;
}
